import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { categories, mainSiteUrl } from "@/mocks/categories";

type NavItem =
  | { key: string; to: string }
  | { key: string; section: string }
  | { key: string; external: string };

const navItems: NavItem[] = [
  { key: "nav.home", to: "/" },
  { key: "nav.spreadsheet", to: "/article" },
  { key: "nav.creators", section: "categories" },
  { key: "nav.reviews", section: "content" },
  { key: "nav.shoppingGuide", external: mainSiteUrl },
];

export default function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);

  function goToItem(item: NavItem) {
    setMenuOpen(false);
    if ("to" in item) {
      navigate(item.to);
      return;
    }
    if ("external" in item) {
      window.open(item.external, "_blank", "noopener,nofollow");
      return;
    }
    const id = item.section;
    if (location.pathname === "/") {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 120);
    }
  }

  return (
    <header className="sticky top-0 z-40 border-b-2 border-background-300 bg-background-50">
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2" onClick={() => setMenuOpen(false)}>
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-grid-line text-lg" />
          </span>
          <span className="whitespace-nowrap font-heading text-lg font-extrabold text-foreground-950">
            {t("common.brand")}
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => {
            const isExternal = "external" in item;
            const isHome = item.key === "nav.home";
            return isExternal ? (
              <a
                key={item.key}
                href={item.external}
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold text-foreground-900 cursor-pointer transition-colors hover:bg-primary-100 hover:text-primary-700"
              >
                <i className="ri-shopping-bag-3-line text-base" />
                {t(item.key)}
              </a>
            ) : (
              <button
                key={item.key}
                type="button"
                onClick={() => goToItem(item)}
                className={`whitespace-nowrap inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold cursor-pointer transition-colors ${
                  isHome
                    ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                    : "text-foreground-900 hover:bg-primary-100 hover:text-primary-700"
                }`}
              >
                {isHome && <i className="ri-home-4-line text-base" />}
                {t(item.key)}
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-md border-2 border-background-300 text-foreground-950 cursor-pointer transition-colors hover:bg-background-100 md:hidden"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menu"
          >
            <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="max-h-[calc(100dvh-4rem)] overflow-y-auto border-t-2 border-background-300 bg-background-50 px-4 py-4 md:hidden">
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => {
              const isExternal = "external" in item;
              return isExternal ? (
                <a
                  key={item.key}
                  href={item.external}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMenuOpen(false)}
                  className="whitespace-nowrap flex items-center gap-2 rounded-lg px-3 py-3 text-left text-base font-semibold text-foreground-900 cursor-pointer transition-colors hover:bg-primary-100"
                >
                  <i className="ri-shopping-bag-3-line text-lg" />
                  {t(item.key)}
                </a>
              ) : (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => goToItem(item)}
                  className="whitespace-nowrap flex items-center gap-2 rounded-lg px-3 py-3 text-left text-base font-semibold text-foreground-900 cursor-pointer transition-colors hover:bg-primary-100"
                >
                  {item.key === "nav.home" && <i className="ri-home-4-line text-lg" />}
                  {t(item.key)}
                </button>
              );
            })}
          </nav>

          <div className="mt-3 border-t border-background-200 pt-3">
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className="flex w-full items-center justify-between rounded-lg px-3 py-3 text-left text-base font-semibold text-foreground-900 cursor-pointer transition-colors hover:bg-primary-100"
              aria-expanded={catOpen}
            >
              <span className="flex items-center gap-2">
                <i className="ri-layout-grid-line text-lg" />
                {t("categories.title")}
              </span>
              <i className={`ri-arrow-down-s-line text-lg transition-transform ${catOpen ? "rotate-180" : ""}`} />
            </button>
            {catOpen && (
              <div className="mt-1 grid grid-cols-2 gap-1 px-1">
                {categories.map((cat) => (
                  <a
                    key={cat.key}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMenuOpen(false)}
                    className="whitespace-nowrap flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-foreground-700 cursor-pointer transition-colors hover:bg-primary-100 hover:text-primary-700"
                  >
                    <i className="ri-arrow-right-up-line text-sm text-foreground-400" />
                    {t(cat.key)}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}